import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Extract error message from API error response
 * Handles both string errors and Pydantic validation error arrays
 */
export function getErrorMessage(error, fallback = 'An error occurred') {
  const detail = error?.response?.data?.detail;
  
  if (!detail) return fallback;
  
  // If it's a string, return directly
  if (typeof detail === 'string') return detail;
  
  // If it's an array of validation errors
  if (Array.isArray(detail)) {
    return detail
      .map(e => e.msg || e.message || (typeof e === 'string' ? e : String(e)))
      .join(', ');
  }
  
  // If it's an object with a message
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  if (typeof detail === 'object' && detail.message) return detail.message;
  
  return fallback;
}

export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

export function formatDate(date) {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatDateInput(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getTransactionTypeLabel(type) {
  const labels = {
    revenue: 'Revenue',
    expense: 'Expense',
    transfer: 'Transfer',
    loan_receipt: 'Loan Received',
    loan_payment: 'Loan Payment',
    tax_payment: 'Tax Payment',
    receivable: 'Receivable',
    receivable_payment: 'Payment Received',
    payable: 'Payable',
    payable_payment: 'Bill Payment'
  };
  return labels[type] || type;
}

export function getStatusColor(status) {
  const colors = {
    paid: 'status-paid',
    due: 'status-due',
    overdue: 'status-overdue',
    voided: 'status-void',
    posted: 'bg-blue-50 text-blue-600',
    active: 'bg-green-50 text-green-600',
    paid_off: 'bg-gray-100 text-gray-600',
    partially_paid: 'status-due'
  };
  return colors[status] || 'bg-gray-100 text-gray-600';
}

/**
 * Show a toast notification when a user tries to perform a restricted action
 * @param {Function} toast - The toast function from sonner
 * @param {string} action - The action that was attempted (e.g., 'create', 'edit', 'delete')
 * @param {string} resource - The resource type (e.g., 'transaction', 'loan', 'receivable')
 */
export function showPermissionDenied(toast, action = 'perform this action', resource = '') {
  const resourceText = resource ? ` on ${resource}` : '';
  toast.error('Permission Denied', {
    description: `You don't have permission to ${action}${resourceText}. Contact an admin to request access.`,
    duration: 4000,
  });
}

/**
 * Check if user can edit and show toast if not
 * @param {boolean} canEdit - Whether the user has edit permissions
 * @param {Function} toast - The toast function from sonner
 * @param {string} action - The action being attempted
 * @param {string} resource - The resource type
 * @returns {boolean} - Whether the action should proceed
 */
export function checkEditPermission(canEdit, toast, action = 'edit', resource = '') {
  if (!canEdit) {
    showPermissionDenied(toast, action, resource);
    return false;
  }
  return true;
}
